// src/services/goalService.ts
import { API_CONFIG } from '../config/apiConfig';

// --- INTERFACES ---
// This interface is now aligned with your backend's camelCase structure
export interface GoalData {
  id: string; // The local UUID from your Node.js backend
  goalOwnerType: string;
  goalType: string;
  goalSubType: string;
  goalName: string;
  goalTarget: string;
  goalTargetTenure: string;
  goalCompletionTentativeAmount: string;
  status?: string;
  familyMemberId?: string;
  jogetId?: string; // Optional: To store the original Joget PK
}

// Defines the paginated response structure from your Node.js backend
export interface GoalsApiResponse {
  status: string;
  data: GoalData[];
  pagination: {
    total: number;
    size: number;
    currentPage: number;
  };
}

// --- AUTHENTICATION HELPER ---
// Standardized helper to get the JWT Bearer token
const getAuthToken = (): string | null => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.error('Authentication Error: JWT Token not found in localStorage.');
    return null;
  }
  return token;
};

// --- API FUNCTIONS ---

/**
 * Fetches all goal records for the logged-in user from your Node.js backend.
 */
export const fetchGoalData = async (page = 1, size = 10): Promise<GoalsApiResponse> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/goals?page=${page}&size=${size}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(errorBody.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();

  } catch (error) {
    console.error('Error fetching goal data:', error);
    throw error;
  }
};

/**
 * Creates or updates a goal record via your Node.js backend.
 */
export const saveOrUpdateGoalRecord = async (goal: GoalData): Promise<any> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  // Construct the payload exactly as the backend controller expects
  const payload = {
    id: goal.id || null, 
    status: goal.status || 'active',
    request_data: {
      goalOwnerType: goal.goalOwnerType,
      goalType: goal.goalType,
      goalSubType: goal.goalSubType,
      goalName: goal.goalName,
      goalTarget: goal.goalTarget,
      goalTargetTenure: goal.goalTargetTenure,
      goalCompletionTentativeAmount: goal.goalCompletionTentativeAmount,
      familyMemberId: goal.familyMemberId,
    }
  };
  
  if (payload.request_data.familyMemberId === '' || payload.request_data.familyMemberId === undefined) {
      delete payload.request_data.familyMemberId;
  }

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/goals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error saving goal record:`, error);
    throw error;
  }
};

/**
 * Soft deletes a goal record by its ID via your Node.js backend.
 */
export const deleteGoalRecord = async (id: string): Promise<{ success: boolean; message: string }> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/goals/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return { success: true, message: result.message };
  } catch (error: any) {
    console.error('Error deleting goal record:', error);
    return { 
      success: false, 
      message: error.message || 'Failed to delete goal record.' 
    };
  }
};